// src/security/v2/apiClient.ts
//
// Permissions v2 API adapter. Uses the shared apiFetch() client from
// src/api/client.ts which handles auth header injection (Bearer JWT or
// X-User-Guid for fake auth) and token refresh.

import { apiFetch } from "../../api/client";
import type {
  CapabilityDto,
  CapabilityName,
  GUID,
  MyGrantsResponse,
  PermissionGrantDto,
  PermissionSetDto,
} from "./types";

// ── Request shapes (match PermissionsController DTOs) ────────

/**
 * Create a project-scoped bundle via the legacy POST /permission-sets
 * endpoint. Kept for backward compat with existing callers; new code
 * should prefer createScopeBundle below for clarity.
 */
export interface CreatePermissionSetRequest {
  name: string;
  description: string;
  projectGuid: GUID;
  capabilities: CapabilityName[];
}

/**
 * Create a grant via the legacy POST /grants endpoint (scope info in body).
 * Now accepts community + collection scopes too — the v1 project-only
 * restriction was lifted in DEV-859 [D2].
 */
export interface CreateGrantRequest {
  permissionSetGuid: GUID;
  scopeType: "project" | "community" | "collection";
  scopeGuid: GUID;
  roleSlotGuid?: GUID | null;
  userGuid?: GUID | null;
  expiresAt?: string | null;
}

/**
 * Body shape for the generalized POST /{scopeType}/{scopeGuid}/permission-sets.
 * Scope info lives in the URL; only bundle-shape fields are in the body.
 */
export interface CreateScopeBundleRequest {
  name: string;
  description: string;
  capabilities: CapabilityName[];
  /**
   * DEV-1038. Grant mode for the new bundle. "user" (governance — granted
   * directly to users) or "slot" (operational — granted to project role
   * slots). Defaults to "user" server-side if omitted.
   */
  grantMode?: "user" | "slot";
}

/**
 * Body shape for PUT /{scopeType}/{scopeGuid}/permission-sets/{permissionSetGuid}.
 * Same fields as create — the new authoritative state of the bundle. System
 * bundles (is_system=TRUE) are rejected by the backend.
 */
export interface UpdateScopeBundleRequest {
  name: string;
  description: string;
  capabilities: CapabilityName[];
  grantMode?: "user" | "slot";
}

/**
 * Body shape for the generalized POST /{scopeType}/{scopeGuid}/grants.
 * Scope info lives in the URL.
 */
export interface CreateScopeGrantRequest {
  permissionSetGuid: GUID;
  roleSlotGuid?: GUID | null;
  userGuid?: GUID | null;
  expiresAt?: string | null;
}

export type PermissionScopeType = "project" | "community" | "collection";

// ── Client surface ───────────────────────────────────────────

export const permissionsApiV2 = {
  // ── Reads (platform-scoped) ──
  capabilities: () => apiFetch<CapabilityDto[]>("/api/permissions/capabilities"),
  permissionSets: () => apiFetch<PermissionSetDto[]>("/api/permissions/permission-sets"),
  myGrants: () => apiFetch<MyGrantsResponse>("/api/permissions/my-grants"),

  /**
   * DEV-861 tier 2. Returns community entity GUIDs that have at least one
   * active "leadership" grant (admin/owner-class bundle, including scoped
   * sysadmin like Rotary Org Admin). Used to render at-a-glance dot
   * indicators in CommunityEntitiesTab.
   */
  communityLeadershipPresence: () =>
    apiFetch<string[]>("/api/permissions/community/leadership-presence"),

  // ── Reads (project-scoped, admin-only) — legacy paths kept for shadow-mode callers ──
  listProjectPermissionSets: (projectGuid: GUID) =>
    apiFetch<PermissionSetDto[]>(
      `/api/permissions/project/${projectGuid}/permission-sets`,
    ),
  listProjectGrants: (projectGuid: GUID) =>
    apiFetch<PermissionGrantDto[]>(
      `/api/permissions/project/${projectGuid}/grants`,
    ),

  // ── Generalized scope-typed reads (DEV-859) ──
  // Use these for new code. Project-scoped reads above still work but the
  // generalized form handles community + collection scopes uniformly.
  listPermissionSetsForScope: (scopeType: PermissionScopeType, scopeGuid: GUID) =>
    apiFetch<PermissionSetDto[]>(
      `/api/permissions/${scopeType}/${scopeGuid}/permission-sets`,
    ),
  listGrantsForScope: (scopeType: PermissionScopeType, scopeGuid: GUID) =>
    apiFetch<PermissionGrantDto[]>(
      `/api/permissions/${scopeType}/${scopeGuid}/grants`,
    ),

  // ── Mutations: legacy paths (admin-only) ──
  createPermissionSet: (req: CreatePermissionSetRequest) =>
    apiFetch<PermissionSetDto>("/api/permissions/permission-sets", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }),
  deletePermissionSet: (permissionSetGuid: GUID) =>
    apiFetch<void>(`/api/permissions/permission-sets/${permissionSetGuid}`, {
      method: "DELETE",
    }),
  createGrant: (req: CreateGrantRequest) =>
    apiFetch<PermissionGrantDto>("/api/permissions/grants", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }),
  revokeGrant: (grantGuid: GUID) =>
    apiFetch<void>(`/api/permissions/grants/${grantGuid}`, {
      method: "DELETE",
    }),

  // ── DEV-892: lifecycle actions ──
  // Recipient (or sysadmin) flips a pending grant to active or declined.
  // Called from the workflow side-effects when an Action Center role-
  // invitation request is approved or rejected by the recipient.
  acceptGrant: (grantGuid: GUID) =>
    apiFetch<{ grantGuid: GUID; status: string }>(
      `/api/permissions/grants/${grantGuid}/accept`,
      { method: "POST" },
    ),
  declineGrant: (grantGuid: GUID) =>
    apiFetch<{ grantGuid: GUID; status: string }>(
      `/api/permissions/grants/${grantGuid}/decline`,
      { method: "POST" },
    ),

  /**
   * DEV-1029 — recipient-initiated grant removal. The grant's user can
   * remove their own grant; same hard-delete semantics as revokeGrant
   * but the audit event records action='resign' so the governance trail
   * distinguishes self-resignation from admin revocation. The frontend
   * caller is responsible for also cleaning the V1 dual-write rows
   * (community_role_assignments + community_memberships) and emitting
   * the community.role_resignation workflow_request that notifies the
   * original granter — mirroring the project resignation pattern.
   */
  resignFromGrant: (grantGuid: GUID) =>
    apiFetch<void>(`/api/permissions/grants/${grantGuid}/resign`, {
      method: "POST",
    }),

  // ── Generalized scope-typed mutations (DEV-859) ──
  createBundleForScope: (
    scopeType: PermissionScopeType,
    scopeGuid: GUID,
    req: CreateScopeBundleRequest,
  ) =>
    apiFetch<PermissionSetDto>(
      `/api/permissions/${scopeType}/${scopeGuid}/permission-sets`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
      },
    ),
  /**
   * DEV-1038. PUT endpoint to edit an existing project-scoped (or community-
   * scoped) bundle. System bundles are rejected. Body is the new authoritative
   * state — name, description, full capability list, and grant mode.
   */
  updateBundleForScope: (
    scopeType: PermissionScopeType,
    scopeGuid: GUID,
    permissionSetGuid: GUID,
    req: UpdateScopeBundleRequest,
  ) =>
    apiFetch<PermissionSetDto>(
      `/api/permissions/${scopeType}/${scopeGuid}/permission-sets/${permissionSetGuid}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
      },
    ),
  createGrantForScope: (
    scopeType: PermissionScopeType,
    scopeGuid: GUID,
    req: CreateScopeGrantRequest,
  ) =>
    apiFetch<PermissionGrantDto>(
      `/api/permissions/${scopeType}/${scopeGuid}/grants`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
      },
    ),

  // DEV-1050 cutover complete — shadow-diff upload methods and types removed
  // along with the V1 evaluator. The shadow_permission_diffs DB table remains
  // (harmless) but no client writes to it.
};
